import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Handshake, Search } from 'lucide-react';
import { format } from 'date-fns';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  Table,
  useToast,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import { useT } from '../i18n';
import type { Booking, Client, Integrator } from '../types/api';
import styles from './Bookings.module.css';

type ActiveFilter = 'all' | 'active' | 'inactive';

export function BookingsPage() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';

  // ── Filter ───────────────────────────────────────────────────────
  const [filter, setFilter] = useState<ActiveFilter>('active');
  const [search, setSearch] = useState('');

  const bookingsKey = ['bookings', filter] as const;
  const bookingsQ = useQuery<Booking[]>({
    queryKey: bookingsKey,
    queryFn: () => {
      const query =
        filter === 'all' ? {} : { active: filter === 'active' };
      return api.get<Booking[]>('/bookings', { query });
    },
  });

  // For the create flow we need full clients + integrators lists. Only the
  // manufacturer can create bookings, so we only fetch these on demand to
  // avoid unnecessary requests for integrator users.
  const clientsQ = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
    enabled: isManufacturer,
  });
  const integratorsQ = useQuery<Integrator[]>({
    queryKey: ['integrators'],
    queryFn: () => api.get<Integrator[]>('/integrators'),
    enabled: isManufacturer,
  });

  const bookings = bookingsQ.data ?? [];

  const filteredBookings = search.trim()
    ? bookings.filter((b) => {
        const q = search.trim().toLowerCase();
        return (
          b.client?.companyName.toLowerCase().includes(q) ||
          b.integrator?.name.toLowerCase().includes(q)
        );
      })
    : bookings;

  // ── Create flow (manufacturer-only) ──────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [selClient, setSelClient] = useState('');
  const [selIntegrator, setSelIntegrator] = useState('');

  const createMutation = useMutation({
    mutationFn: (payload: { clientId: string; integratorId: string }) =>
      api.post<Booking>('/bookings', payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast.success(
        t('bookings.toastCreated'),
        `${created.client?.companyName ?? t('bookings.clientFallback')} → ${created.integrator?.name ?? t('bookings.integratorFallback')}`,
      );
      setCreateOpen(false);
      setSelClient('');
      setSelIntegrator('');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('bookings.errorCreate');
      toast.error(t('bookings.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    if (!selClient || !selIntegrator) return;
    createMutation.mutate({ clientId: selClient, integratorId: selIntegrator });
  };

  // ── Release (deactivate) flow (manufacturer-only) ────────────────
  const releaseMutation = useMutation({
    mutationFn: (id: string) => api.patch<Booking>(`/bookings/${id}/deactivate`),
    onSuccess: (released) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast.success(
        t('bookings.toastReleased'),
        `${released.client?.companyName ?? ''}`.trim() || undefined,
      );
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('bookings.errorRelease');
      toast.error(t('bookings.toastReleaseFail'), msg);
    },
  });

  const errorMsg = bookingsQ.error instanceof Error ? bookingsQ.error.message : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('bookings.title')}
        description={
          isManufacturer ? t('bookings.descManufacturer') : t('bookings.descIntegrator')
        }
        action={
          isManufacturer && (
            <Button leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
              {t('bookings.book')}
            </Button>
          )
        }
      />

      <div className={styles.toolbar}>
        <Input
          aria-label={t('bookings.searchAria')}
          placeholder={t('bookings.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search size={16} />}
          fullWidth
          className={styles.search}
        />
        <Select
          aria-label={t('bookings.filterAria')}
          value={filter}
          onChange={(e) => setFilter(e.target.value as ActiveFilter)}
          options={[
            { value: 'active', label: t('bookings.filterActive') },
            { value: 'inactive', label: t('bookings.filterInactive') },
            { value: 'all', label: t('bookings.filterAll') },
          ]}
          className={styles.filter}
        />
      </div>

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {bookingsQ.isLoading ? (
        <BookingsSkeleton showActions={isManufacturer} />
      ) : filteredBookings.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Handshake size={28} />}
            title={search.trim() ? t('bookings.emptySearchTitle') : t('bookings.emptyTitle')}
            description={
              search.trim()
                ? t('bookings.emptySearchDesc')
                : isManufacturer
                ? t('bookings.emptyDescManufacturer')
                : t('bookings.emptyDescIntegrator')
            }
            action={
              isManufacturer &&
              !search.trim() && (
                <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                  {t('bookings.book')}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('bookings.colClient')}</th>
              <th>{t('bookings.colIntegrator')}</th>
              <th>{t('bookings.colStatus')}</th>
              <th>{t('bookings.colBookedAt')}</th>
              {isManufacturer && <th className="actions" />}
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((b) => (
              <tr key={b.id}>
                <td className={styles.cellBold}>{b.client?.companyName ?? '—'}</td>
                <td>{b.integrator?.name ?? '—'}</td>
                <td>
                  <StatusPill tone={b.active ? 'success' : 'neutral'} subtle={!b.active}>
                    {b.active ? 'Active' : 'Released'}
                  </StatusPill>
                </td>
                <td className={styles.cellDate}>
                  {format(new Date(b.bookedAt), 'yyyy-MM-dd HH:mm')}
                </td>
                {isManufacturer && (
                  <td className="actions">
                    {b.active ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => releaseMutation.mutate(b.id)}
                        loading={
                          releaseMutation.isPending &&
                          releaseMutation.variables === b.id
                        }
                      >
                        {t('bookings.release')}
                      </Button>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Book Client modal ──────────────────────────────────── */}
      {isManufacturer && (
        <Modal
          open={createOpen}
          onClose={() => !createMutation.isPending && setCreateOpen(false)}
          title={t('bookings.modalTitle')}
          description={t('bookings.modalDesc')}
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setCreateOpen(false)}
                disabled={createMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                form="create-booking-form"
                loading={createMutation.isPending}
                disabled={!selClient || !selIntegrator}
              >
                {t('bookings.bookSubmit')}
              </Button>
            </>
          }
        >
          <form id="create-booking-form" onSubmit={submitCreate} className={styles.form} noValidate>
            <Select
              label={t('bookings.clientLabel')}
              value={selClient}
              onChange={(e) => setSelClient(e.target.value)}
              placeholder={t('bookings.selectClient')}
              required
              fullWidth
              disabled={clientsQ.isLoading}
              options={(clientsQ.data ?? []).map((c) => ({
                value: c.id,
                label: c.companyName,
              }))}
            />
            <Select
              label={t('bookings.integratorLabel')}
              value={selIntegrator}
              onChange={(e) => setSelIntegrator(e.target.value)}
              placeholder={t('bookings.selectIntegrator')}
              required
              fullWidth
              disabled={integratorsQ.isLoading}
              helperText={t('bookings.onlyActiveIntegrators')}
              options={(integratorsQ.data ?? [])
                .filter((i) => i.active)
                .map((i) => ({
                  value: i.id,
                  label: i.name,
                }))}
            />
          </form>
        </Modal>
      )}
    </div>
  );
}

function BookingsSkeleton({ showActions }: { showActions: boolean }) {
  const { t } = useT();
  const cols = showActions ? 5 : 4;
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('bookings.colClient')}</th>
          <th>{t('bookings.colIntegrator')}</th>
          <th>{t('bookings.colStatus')}</th>
          <th>{t('bookings.colBookedAt')}</th>
          {showActions && <th className="actions" />}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: cols }).map((__, j) => (
              <td key={j}><Skeleton width="80%" /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
